import React, { useCallback, useEffect, useRef } from 'react'
import { Form, Input, Typography, Space, Button, Card } from 'antd'
import { PlayCircleOutlined } from '@ant-design/icons'
import { StepComponentProps, OnboardingV2Data } from '../index'
import IntlMessages from '../../../util/IntlMessages'
import Api from '@vacationtracker/shared/services/api'
import { useOnboardingContext, useMarkStepCompleted } from '../context'
import logger from '@vacationtracker/shared/functions/logger'
import { track } from '../../../services/analytics/analytics'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import useDebounce from '../../../util/use-debounce'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'

const { Title, Paragraph, Text } = Typography

export const DepartmentSetupStep: React.FC<StepComponentProps<OnboardingV2Data>> = ({ onNext, errors }) => {
  const { dispatch: updateData, formData: data, isLoading } = useOnboardingContext()
  const markStepCompleted = useMarkStepCompleted()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const initialNameRef = useRef<string>(data.authUser?.team?.name || '')

  const debouncedDepartmentName = useDebounce(data.departmentName, 1000)

  // Users and approvers loaded via getCompanyAndUserInfo (include imageUrl)

  // Screen shown
  useEffect(() => {
    if (authCompany && data?.authUser) {
      track('ONBOARDING_STEP_2_DEPARTMENT_SHOWN', {
        company_id: authCompany.id,
        user_id: data.authUser.id,
      })
    }
  }, [authCompany, data?.authUser])

  // Track edits only after user stops typing
  useEffect(() => {
    if (!authCompany) return
    if (debouncedDepartmentName === initialNameRef.current) return

    track('ONBOARDING_STEP_2_DEPARTMENT_NAME_EDITED', {
      original_name: initialNameRef.current,
      new_name: debouncedDepartmentName,
      company_id: authCompany.id,
    })
  }, [debouncedDepartmentName, authCompany])

  const onNextHandler = () => void handleNext()

  const handleNext = useCallback(async () => {
    if (!data.departmentName || !onNext || !data.authUser) return

    try {
      updateData({ type: 'SET_LOADING', payload: true })

      if (authCompany) {
        track('ONBOARDING_STEP_2_CONTINUE_CLICKED', {
          company_id: authCompany.id,
          department_name: data.departmentName,
        })
      }

      // Make TEAM_CREATED API call
      await Api.post('/core/event', {
        eventType: 'TEAM_UPDATED',
        eventGroup: 'TEAM',
        teamId: data.authUser.team?.id || '', // Required
        name: data.departmentName, // Required
        users: [data.authUser.id], // Required array of user IDs
        approvers: data.authUser.team?.approvers
          ? data.authUser.team.approvers.map((approver) => ({
              userId: approver.id,
              approverLevels: ['FIRST'],
            }))
          : [{ userId: data.authUser.id, approverLevels: ['FIRST'] }], // At least one approver is required
        version: 2, // Required
        isDefault: true,
        platform: data.authUser.platform,
      })

      // Mark step as completed (track relevant steps only)
      markStepCompleted('department-setup')

      await onNext()
    } catch (error) {
      logger.error('Error creating team:', error)
      updateData({
        type: 'SET_ERRORS',
        payload: { department: 'Failed to create department. Please try again.' },
      })
    } finally {
      updateData({ type: 'SET_LOADING', payload: false })
    }
  }, [data.departmentName, onNext, data.authUser, updateData, authCompany, markStepCompleted])

  // No extra fetch needed; users/approvers come from getCompanyAndUserInfo

  return (
    <div className="department-setup-step">
      <div className="step-content">
        <div className="step-left-section">
          <div style={{ maxWidth: 480, margin: '0 auto', width: '100%' }}>
            <div style={{ marginBottom: 40 }}>
              <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 24 }}>
                <IntlMessages id="onboarding.department.stepIndicator" />
              </Text>
              <Title level={2} style={{ fontSize: 32, marginBottom: 16 }}>
                <IntlMessages id="onboarding.department.title" />
              </Title>
              <Paragraph style={{ color: '#666', fontSize: 16, marginBottom: 24 }}>
                <IntlMessages id="onboarding.department.description" />
              </Paragraph>

              <Form layout="vertical">
                <Form.Item
                  label={<IntlMessages id="onboarding.department.nameLabel" />}
                  required
                  validateStatus={errors?.department ? 'error' : undefined}
                  help={errors?.department}
                >
                  <Input
                    placeholder="Department name"
                    size="large"
                    value={data.departmentName}
                    onChange={(e) => {
                      const newName = e.target.value
                      updateData({ type: 'UPDATE_FORM_DATA', payload: { departmentName: newName } })
                    }}
                    onBlur={() => {
                      if (authCompany && data.departmentName !== initialNameRef.current) {
                        track('ONBOARDING_STEP_2_DEPARTMENT_NAME_EDITED', {
                          original_name: initialNameRef.current,
                          new_name: data.departmentName,
                          company_id: authCompany.id,
                        })
                      }
                    }}
                    style={{ maxWidth: 400 }}
                  />
                </Form.Item>
              </Form>

              <Text type="secondary" style={{ display: 'block', marginBottom: 40 }}>
                {authCompany?.plan === SubscriptionPlanEnum.freemium ? <IntlMessages id="onboarding.department.freemiumNote" /> : <IntlMessages id="onboarding.department.note" />}
              </Text>

              <Button type="primary" size="large" onClick={onNextHandler} disabled={!data.departmentName} loading={isLoading}>
                <IntlMessages id="onboarding.department.continue" />
              </Button>
            </div>

            <Card className="onboarding-video-card">
              <Space align="start" size={16}>
                <a
                  href="https://www.youtube.com/watch?v=LbRERNRptu8"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open departments video"
                  onClick={() => {
                    if (authCompany) {
                      track('ONBOARDING_STEP_2_VIDEO_TUTORIAL_OPENED', { company_id: authCompany.id })
                    }
                  }}
                  style={{
                    display: 'block',
                    width: 96,
                    height: 54,
                    borderRadius: 8,
                    overflow: 'hidden',
                    position: 'relative',
                    flexShrink: 0,
                  }}
                >
                  <img
                    src="https://img.youtube.com/vi/LbRERNRptu8/hqdefault.jpg"
                    alt="Departments video thumbnail"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <PlayCircleOutlined
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: 28,
                      color: '#ffffff',
                      textShadow: '0 2px 6px rgba(0,0,0,0.6)',
                    }}
                  />
                </a>
                <div>
                  <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                    <IntlMessages id="onboarding.department.about.title" />
                  </Text>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    <IntlMessages id="onboarding.department.about.description" />
                  </Text>
                  <Button type="link" style={{ padding: 0 }} onClick={() => window.open('https://www.youtube.com/watch?v=LbRERNRptu8', '_blank', 'noopener')}>
                    <IntlMessages id="onboarding.department.about.learnMore" />
                  </Button>
                </div>
              </Space>
            </Card>
          </div>
        </div>

        <div className="step-right-section">
          <div style={{ maxWidth: 480, margin: '0 auto', width: '100%' }}>
            <Card className="team-info-card" style={{ minHeight: 300 }}>
              <Card.Meta
                title={<IntlMessages id="onboarding.department.preview.title" values={{ departmentName: data.departmentName || 'No department set' }} />}
                description={
                  <div className="team-details">
                    <div className="subsection" style={{ marginBottom: 24 }}>
                      <Text strong className="subsection-title" style={{ fontSize: 16, marginBottom: 12, display: 'block' }}>
                        <IntlMessages id="onboarding.department.preview.approvers" />
                      </Text>
                      <div className="subsection-content">
                        {data.authUser?.team?.approvers && data.authUser.team.approvers.length > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {data.authUser.team.approvers.slice(0, 4).map((a) => (
                              <span key={a.id} className="user-avatar">
                                <UserAvatar
                                  id={a.id}
                                  name={a.name}
                                  avatar={a.imageUrl}
                                  avatarSize="small"
                                  isShowingName={false}
                                  isTooltip={true}
                                  isLinkToPage={false}
                                  shape="circle"
                                />
                              </span>
                            ))}
                            {data.authUser.team.approvers.length > 4 && <span className="onb-inline-count">+{data.authUser.team.approvers.length - 4}</span>}
                          </div>
                        ) : (
                          <Text type="secondary">
                            <IntlMessages id="onboarding.department.preview.noApprovers" />
                          </Text>
                        )}
                      </div>
                    </div>

                    <div className="subsection">
                      <Text strong className="subsection-title" style={{ fontSize: 16, marginBottom: 12, display: 'block' }}>
                        <IntlMessages id="onboarding.department.preview.users" />
                      </Text>
                      <div className="subsection-content">
                        {data.authUser?.team?.users && data.authUser.team.users.length > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {data.authUser.team.users.slice(0, 4).map((user) => (
                              <span key={user.id} className="user-avatar">
                                <UserAvatar
                                  id={user.id}
                                  name={user.name}
                                  avatar={user.imageUrl}
                                  avatarSize="small"
                                  isShowingName={false}
                                  isTooltip={true}
                                  isLinkToPage={false}
                                  shape="circle"
                                />
                              </span>
                            ))}
                            {data.authUser.team.users.length > 4 && <span className="onb-inline-count">+{data.authUser.team.users.length - 4}</span>}
                          </div>
                        ) : data.authUser ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <UserAvatar
                              id={data.authUser.id}
                              name={data.authUser.name}
                              avatar={data.authUser.imageUrl}
                              avatarSize="small"
                              isShowingName={false}
                              isTooltip={true}
                              isLinkToPage={false}
                              shape="circle"
                            />
                          </div>
                        ) : (
                          <Text type="secondary">
                            <IntlMessages id="onboarding.department.preview.noUsers" />
                          </Text>
                        )}
                      </div>
                    </div>
                  </div>
                }
              />
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
